import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const HormigonRallado = () => {
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('HormiPremium')
          .list('Rallado', {
            limit: 6,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (error) throw error;

        if (data) {
          const imageFiles = data
            .filter((file) => {
              const ext = file.name.toLowerCase();
              return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
            })
            .slice(0, 6)
            .map((file) => {
              const fileName = encodeURIComponent(file.name);
              return `${SUPABASE_URL}/storage/v1/object/public/HormiPremium/Rallado/${fileName}`;
            });

          setImages(imageFiles);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();
  }, []);

  const features = [
    {
      icon: 'ri-contrast-2-line',
      title: 'Textura Antideslizante',
      description: 'Superficie rayada que proporciona máxima adherencia y seguridad',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Drenaje Perfecto',
      description: 'Las ranuras facilitan la evacuación del agua evitando charcos',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Alta Resistencia',
      description: 'Ideal para zonas de alto tránsito y uso intensivo',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Acabado Moderno',
      description: 'Estética contemporánea perfecta para espacios exteriores',
    },
  ];

  const applications = [
    'Rampas de acceso',
    'Zonas de piscina',
    'Entradas de garaje',
    'Terrazas y patios',
    'Caminos peatonales',
    'Áreas comerciales',
  ];

  return (
    <section id="hormigon-rallado" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-contrast-2-line"></i>
              <span>Textura Antideslizante</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">
              Hormigón Rallado
            </h2>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              El <strong>hormigón rallado</strong> es la solución perfecta cuando necesitas combinar <strong>seguridad y estética</strong>. Su superficie con ranuras paralelas proporciona <strong>máxima adherencia antideslizante</strong>, ideal para rampas, zonas de piscina y áreas donde la seguridad es prioritaria.
            </p>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              Este acabado no solo es funcional, sino también muy <strong>decorativo</strong>. Las líneas ralladas crean un patrón visual moderno y elegante que se adapta perfectamente a cualquier diseño arquitectónico contemporáneo.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-amber-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-dark mb-1">{feature.title}</h4>
                    <p className="text-secondary text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/servicios/hormigon-rallado"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Ver detalles completos
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {images.length > 0 ? (
              images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className={`rounded-xl overflow-hidden shadow-lg ${
                    index === 0 ? 'col-span-2 h-80' : 'h-64'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Hormigón Rallado ${index + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))
            ) : (
              <div className="col-span-2 h-80 bg-gray-200 rounded-xl flex items-center justify-center">
                <p className="text-gray-500">Cargando imágenes...</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-dark mb-6">
            Aplicaciones del Hormigón Rallado
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {applications.map((app, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-amber-600 font-bold"></i>
                </div>
                <span className="text-dark font-medium">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HormigonRallado;
